/**
 * Globally locks background scroll when modal overlays are open.
 * Mirrors PrimeReact Dialog `blockScroll` (p-overflow-hidden + --scrollbar-width)
 * so the page does not shift horizontally when the scrollbar is hidden.
 */

const SCROLL_LOCK_CLASS = 'p-overflow-hidden';
const GLOBAL_LOCK_ATTR = 'data-virtup-global-scroll-lock';
const HTML_LOCK_ATTR = 'data-virtup-html-scroll-lock';
const SCROLL_Y_ATTR = 'data-virtup-scroll-y';
const SCROLL_LOCK_Y_VAR = '--virtup-scroll-lock-y';
const STICKY_PIN_ATTR = 'data-virtup-sticky-pin';
const STICKY_SPACER_ATTR = 'data-virtup-sticky-spacer';

/**
 * Sticky sidebars break when body is frozen with position:fixed (scrollY becomes 0).
 * Capture their viewport rect before freeze and pin with position:fixed for the lock.
 * Project detail only — do not pin sticky panels in other modules.
 */
const STICKY_SIDEBAR_SELECTORS = ['.project-detail__right'] as const;

const MODAL_OVERLAY_SELECTORS = [
  '.p-dialog-mask',
  '.virtup-sidebar-overlay',
  '[class*="-modal-overlay"]',
  '.event-details-backdrop',
  '.slot-event-detail-overlay',
] as const;

const PENDING_OPEN_SCROLL_MS = 400;
const RESTORE_GUARD_MS = 350;

type StickyPinSnapshot = {
  element: HTMLElement;
  top: number;
  left: number;
  width: number;
  height: number;
};

declare global {
  interface Document {
    primeDialogParams?: Array<{ id: string; hasBlockScroll: boolean }>;
  }
}

let syncScheduled = false;
let observer: MutationObserver | null = null;
let bodyObserver: MutationObserver | null = null;
let cachedScrollbarWidth = 0;
let lockedScrollY = 0;
let pendingOpenScrollY: number | null = null;
let pendingOpenScrollTimer: ReturnType<typeof setTimeout> | null = null;
let pendingStickySnapshots: StickyPinSnapshot[] | null = null;
let restoreGuardY = 0;
let restoreGuardUntil = 0;

const measureBodyScrollbarWidth = (): number =>
  window.innerWidth - document.documentElement.offsetWidth;

const refreshCachedScrollbarWidth = (): void => {
  const measured = measureBodyScrollbarWidth();
  if (measured > 0) {
    cachedScrollbarWidth = measured;
  }
};

const getBodyScrollbarWidth = (): number => {
  const measured = measureBodyScrollbarWidth();
  if (measured > 0) {
    cachedScrollbarWidth = measured;
    return measured;
  }

  return cachedScrollbarWidth;
};

const isOverlayActive = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  // PrimeReact dialog masks stay in the DOM during enter/leave transitions (opacity can be 0).
  if (element.classList.contains('p-dialog-mask')) {
    return element.isConnected;
  }

  if (parseFloat(style.opacity) === 0) {
    return false;
  }

  return element.getClientRects().length > 0;
};

const hasVisibleModalOverlay = (): boolean =>
  MODAL_OVERLAY_SELECTORS.some((selector) =>
    Array.from(document.querySelectorAll(selector)).some((element) =>
      isOverlayActive(element),
    ),
  );

const BOOKING_MOBILE_MENU_LOCK_ATTR = 'data-virtup-booking-mobile-menu-lock';

export const setBookingMobileMenuScrollLock = (locked: boolean): void => {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }

  if (locked) {
    document.body.setAttribute(BOOKING_MOBILE_MENU_LOCK_ATTR, 'true');
  } else {
    document.body.removeAttribute(BOOKING_MOBILE_MENU_LOCK_ATTR);
  }

  scheduleSyncGlobalScrollLock();
};

const hasAuxiliaryScrollLock = (): boolean =>
  document.body.getAttribute(BOOKING_MOBILE_MENU_LOCK_ATTR) === 'true';

const getScrollY = (): number =>
  window.scrollY || document.documentElement.scrollTop || 0;

const armPendingOpenScroll = (): void => {
  if (document.body?.getAttribute(GLOBAL_LOCK_ATTR) === 'true') {
    return;
  }

  pendingOpenScrollY = getScrollY();
  // Snapshot sticky sidebars before dialog open / body freeze mutates layout.
  pendingStickySnapshots = captureStickySidebarSnapshots();

  if (pendingOpenScrollTimer) {
    clearTimeout(pendingOpenScrollTimer);
  }

  pendingOpenScrollTimer = setTimeout(() => {
    pendingOpenScrollY = null;
    pendingStickySnapshots = null;
    pendingOpenScrollTimer = null;
  }, PENDING_OPEN_SCROLL_MS);
};

/** Locked page scroll offset — used to correct PrimeReact overlays appended to document.body. */
export const getGlobalScrollLockOffsetY = (): number => {
  if (document.body?.getAttribute(GLOBAL_LOCK_ATTR) !== 'true') {
    return 0;
  }

  const storedY = document.body.getAttribute(SCROLL_Y_ATTR);
  const parsedStoredY = storedY != null ? parseInt(storedY, 10) : Number.NaN;
  if (Number.isFinite(parsedStoredY) && parsedStoredY >= 0) {
    return parsedStoredY;
  }

  return lockedScrollY > 0 ? lockedScrollY : 0;
};

const setScrollLockOverlayOffsetVar = (scrollY: number): void => {
  document.body.style.setProperty(SCROLL_LOCK_Y_VAR, `${Math.max(0, scrollY)}px`);
};

const clearScrollLockOverlayOffsetVar = (): void => {
  document.body.style.removeProperty(SCROLL_LOCK_Y_VAR);
};

const resolveScrollYForLock = (): number => {
  const live = getScrollY();
  if (live > 0) {
    return live;
  }

  if (pendingOpenScrollY != null && pendingOpenScrollY > 0) {
    return pendingOpenScrollY;
  }

  if (lockedScrollY > 0) {
    return lockedScrollY;
  }

  return 0;
};

const restoreScrollYInstant = (scrollY: number): void => {
  if (!Number.isFinite(scrollY) || scrollY <= 0) {
    return;
  }

  const html = document.documentElement;
  html.style.scrollBehavior = 'auto';
  html.scrollTop = scrollY;
  window.scrollTo(0, scrollY);
};

const scheduleScrollRestore = (scrollY: number): void => {
  if (!Number.isFinite(scrollY) || scrollY <= 0) {
    return;
  }

  restoreGuardY = scrollY;
  restoreGuardUntil = Date.now() + RESTORE_GUARD_MS;

  restoreScrollYInstant(scrollY);

  requestAnimationFrame(() => {
    restoreScrollYInstant(scrollY);
    requestAnimationFrame(() => {
      restoreScrollYInstant(scrollY);
      setTimeout(() => {
        restoreScrollYInstant(scrollY);
        document.documentElement.style.removeProperty('scroll-behavior');
      }, 0);
    });
  });

  setTimeout(() => {
    if (Date.now() >= restoreGuardUntil) {
      restoreGuardY = 0;
      restoreGuardUntil = 0;
    }
  }, RESTORE_GUARD_MS + 50);
};

const handleScrollRestoreGuard = (): void => {
  if (restoreGuardUntil <= 0 || Date.now() > restoreGuardUntil) {
    return;
  }

  if (getScrollY() + 2 < restoreGuardY) {
    restoreScrollYInstant(restoreGuardY);
  }
};

const captureStickySidebarSnapshots = (): StickyPinSnapshot[] => {
  const snapshots: StickyPinSnapshot[] = [];

  // Sticky columns only apply on desktop grid; skip stacked mobile layout.
  if (window.matchMedia('(max-width: 991.98px)').matches) {
    return snapshots;
  }

  STICKY_SIDEBAR_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) {
        return;
      }

      if (node.getAttribute(STICKY_PIN_ATTR) === 'true') {
        return;
      }

      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return;
      }

      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }

      snapshots.push({
        element: node,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    });
  });

  return snapshots;
};

const applyStickySidebarPins = (snapshots: StickyPinSnapshot[]): void => {
  snapshots.forEach(({ element, top, left, width, height }) => {
    if (!element.isConnected || element.getAttribute(STICKY_PIN_ATTR) === 'true') {
      return;
    }

    const spacer = document.createElement('div');
    spacer.setAttribute(STICKY_SPACER_ATTR, 'true');
    spacer.setAttribute('aria-hidden', 'true');
    // Keep Bootstrap column width classes; drop sticky identity class so the
    // spacer stays in normal flow and does not get re-pinned.
    spacer.className = Array.from(element.classList)
      .filter((className) => className !== 'project-detail__right')
      .join(' ');
    spacer.style.height = `${height}px`;
    spacer.style.visibility = 'hidden';
    spacer.style.pointerEvents = 'none';

    element.parentNode?.insertBefore(spacer, element);

    element.setAttribute(STICKY_PIN_ATTR, 'true');
    element.style.setProperty('position', 'fixed', 'important');
    element.style.setProperty('top', `${top}px`, 'important');
    element.style.setProperty('left', `${left}px`, 'important');
    element.style.setProperty('width', `${width}px`, 'important');
    element.style.setProperty('right', 'auto', 'important');
    element.style.setProperty('margin', '0', 'important');
  });
};

const clearStickySidebarPins = (): void => {
  document.querySelectorAll(`[${STICKY_PIN_ATTR}="true"]`).forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    node.removeAttribute(STICKY_PIN_ATTR);
    node.style.removeProperty('position');
    node.style.removeProperty('top');
    node.style.removeProperty('left');
    node.style.removeProperty('width');
    node.style.removeProperty('right');
    node.style.removeProperty('margin');
  });

  document.querySelectorAll(`[${STICKY_SPACER_ATTR}="true"]`).forEach((node) => {
    node.parentNode?.removeChild(node);
  });
};

const freezeBodyScrollPosition = (): void => {
  const scrollY = resolveScrollYForLock();
  lockedScrollY = scrollY;
  const fullHeight = document.body.scrollHeight;

  document.body.setAttribute(SCROLL_Y_ATTR, String(scrollY));

  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.removeProperty('width');
  // Height is only needed while position:fixed is active; never restore it on unlock.
  document.body.style.height = `${fullHeight}px`;
};

const clearBodyScrollLockInlineStyles = (): void => {
  document.body.style.removeProperty('position');
  document.body.style.removeProperty('top');
  document.body.style.removeProperty('left');
  document.body.style.removeProperty('right');
  document.body.style.removeProperty('width');
  document.body.style.removeProperty('height');
};

const unfreezeBodyScrollPosition = (): number => {
  const storedY = document.body.getAttribute(SCROLL_Y_ATTR);
  const parsedStoredY = storedY != null ? parseInt(storedY, 10) : Number.NaN;
  const scrollY = Number.isFinite(parsedStoredY)
    ? parsedStoredY
    : lockedScrollY > 0
      ? lockedScrollY
      : getScrollY();

  clearBodyScrollLockInlineStyles();
  document.body.removeAttribute(SCROLL_Y_ATTR);
  document.body.removeAttribute('data-virtup-body-height');

  return Number.isFinite(scrollY) ? scrollY : 0;
};

/** Clears stale inline scroll-lock styles left after dialog close (e.g. height without position:fixed). */
const cleanupStaleBodyScrollLockStyles = (): void => {
  if (
    document.body.getAttribute(GLOBAL_LOCK_ATTR) === 'true' ||
    hasVisibleModalOverlay() ||
    hasAuxiliaryScrollLock()
  ) {
    return;
  }

  clearStickySidebarPins();

  const hasScrollLockPosition = document.body.style.position === 'fixed';
  const hasScrollLockHeight = Boolean(document.body.style.height);

  if (!hasScrollLockPosition && !hasScrollLockHeight) {
    return;
  }

  clearBodyScrollLockInlineStyles();
  document.body.removeAttribute(SCROLL_Y_ATTR);
  document.body.removeAttribute('data-virtup-body-height');
};

const lockHtmlScroll = (): void => {
  document.documentElement.style.overflow = 'hidden';
  document.documentElement.setAttribute(HTML_LOCK_ATTR, 'true');
};

const unlockHtmlScroll = (): void => {
  if (document.documentElement.getAttribute(HTML_LOCK_ATTR) !== 'true') {
    return;
  }

  document.documentElement.style.removeProperty('overflow');
  document.documentElement.removeAttribute(HTML_LOCK_ATTR);
};

const applyGlobalScrollLock = (): void => {
  const scrollbarWidth = getBodyScrollbarWidth();
  const isAlreadyLocked = document.body.getAttribute(GLOBAL_LOCK_ATTR) === 'true';

  if (!isAlreadyLocked) {
    // Prefer click-time snapshots (sticky still correct); fall back to live capture.
    const stickySnapshots =
      pendingStickySnapshots && pendingStickySnapshots.length > 0
        ? pendingStickySnapshots
        : captureStickySidebarSnapshots();
    pendingStickySnapshots = null;
    freezeBodyScrollPosition();
    applyStickySidebarPins(stickySnapshots);
  }

  document.body.style.removeProperty('overflow');
  document.body.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
  document.body.style.touchAction = 'none';
  lockHtmlScroll();

  if (!document.body.classList.contains(SCROLL_LOCK_CLASS)) {
    document.body.classList.add(SCROLL_LOCK_CLASS);
  }

  document.body.setAttribute(GLOBAL_LOCK_ATTR, 'true');
  setScrollLockOverlayOffsetVar(getGlobalScrollLockOffsetY());
};

const releaseGlobalScrollLock = (): void => {
  if (hasVisibleModalOverlay() || hasAuxiliaryScrollLock()) {
    return;
  }

  if (document.body.getAttribute(GLOBAL_LOCK_ATTR) !== 'true') {
    return;
  }

  clearStickySidebarPins();
  const scrollY = unfreezeBodyScrollPosition();

  document.body.classList.remove(SCROLL_LOCK_CLASS);
  document.body.style.removeProperty('--scrollbar-width');
  document.body.style.removeProperty('touch-action');
  document.body.removeAttribute(GLOBAL_LOCK_ATTR);
  clearScrollLockOverlayOffsetVar();
  unlockHtmlScroll();

  scheduleScrollRestore(scrollY);
  lockedScrollY = 0;
  pendingOpenScrollY = null;
  pendingStickySnapshots = null;

  if (pendingOpenScrollTimer) {
    clearTimeout(pendingOpenScrollTimer);
    pendingOpenScrollTimer = null;
  }
};

const syncGlobalScrollLock = (): void => {
  syncScheduled = false;

  if (hasVisibleModalOverlay() || hasAuxiliaryScrollLock()) {
    applyGlobalScrollLock();
    return;
  }

  releaseGlobalScrollLock();
  cleanupStaleBodyScrollLockStyles();
};

const scheduleSyncGlobalScrollLock = (): void => {
  syncGlobalScrollLock();

  if (syncScheduled) {
    return;
  }

  syncScheduled = true;
  requestAnimationFrame(() => {
    syncScheduled = false;
    syncGlobalScrollLock();
  });
};

const startGlobalScrollLock = (): void => {
  if (!document.body || observer) {
    return;
  }

  refreshCachedScrollbarWidth();
  window.addEventListener('resize', refreshCachedScrollbarWidth);
  window.addEventListener('scroll', handleScrollRestoreGuard, { passive: true });
  document.addEventListener('click', armPendingOpenScroll, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      armPendingOpenScroll();
    }
  }, true);

  observer = new MutationObserver(scheduleSyncGlobalScrollLock);
  observer.observe(document.body, { childList: true, subtree: true });

  bodyObserver = new MutationObserver(scheduleSyncGlobalScrollLock);
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class', 'style'],
  });

  syncGlobalScrollLock();
};

export const installGlobalScrollLock = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startGlobalScrollLock, { once: true });
    return;
  }

  startGlobalScrollLock();
};

installGlobalScrollLock();

